import { useEffect, useState } from "react";
import { notificatonActionEnum, notificatonStatusEnum } from "../../utils/const";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMoreNotifications } from "core/redux/notifications/notificationSlice";

const DISPLAY_BATCH = 5;
const API_BATCH_SIZE = 100;

interface NotificationItemData {
  hs?: string;
  country_desc?: string;
  direction?: string;
  duties_count?: number;
  controls_count?: number;
  sku_count?: number;
}

interface NotificationItem {
  id: string;
  feature: string;
  ui_id?: string;
  created_by?: string;
  assigned_to?: string;
  status: string;
  updated_at?: string;
  type?: string;
  redirect_id?: string;
  transaction_id?: string;
  data?: NotificationItemData;
}

interface NotificationsData {
  data?: NotificationItem[];
  unread_count?: number;
  total_count?: number;
}

interface NotificationModalProps {
  setShowNotification: (value: boolean) => void;
  notificationData: NotificationsData;
  notificationsUpdate: (action: string, data: NotificationItem | null) => void;
  unreadCount?: number;
  totalCount?: number;
}

interface ChildNavigationItem {
  label: string;
  activeTab: string;
  activeSubTab: string;
}

const NotificationModal = ({ setShowNotification, notificationData, notificationsUpdate, unreadCount, totalCount }: NotificationModalProps) => {
  const [modalData, setModalData] = useState<NotificationItem[]>([]);
  const [displayCount, setDisplayCount] = useState(DISPLAY_BATCH);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentFrom = useSelector((state: any) => state.notifications.currentFrom);
  const isFetchingMore = useSelector((state: any) => state.notifications.isFetchingMore);

  useEffect(() => {
    if (notificationData?.data && notificationData.data.length > 0) {
      setModalData(notificationData.data.slice(0, DISPLAY_BATCH));
      setDisplayCount(DISPLAY_BATCH);
    } else {
      setModalData([]);
      setDisplayCount(DISPLAY_BATCH);
    }
  }, [notificationData?.data]);

  const allFetchedData = notificationData?.data || [];
  const nextApiFrom = currentFrom + API_BATCH_SIZE;
  const showLoadMore = displayCount < allFetchedData.length || (totalCount !== undefined && totalCount > nextApiFrom && allFetchedData.length >= nextApiFrom);

  const loadMoreHandler = () => {
    const nextDisplayCount = displayCount + DISPLAY_BATCH;
    if (nextDisplayCount <= allFetchedData.length) {
      setModalData(allFetchedData.slice(0, nextDisplayCount));
      setDisplayCount(nextDisplayCount);
      return;
    }
    if (allFetchedData.length > displayCount) {
      setModalData(allFetchedData.slice(0, allFetchedData.length));
      setDisplayCount(allFetchedData.length);
      return;
    }
    if (totalCount !== undefined && totalCount > nextApiFrom) {
      dispatch(fetchMoreNotifications(nextApiFrom) as any).then(() => {
        setDisplayCount(nextDisplayCount);
      });
    }
  };

  useEffect(() => {
    if (allFetchedData.length > 0) {
      setModalData(allFetchedData.slice(0, displayCount));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFetchedData.length]);

  const setHeadingHandler = (item: NotificationItem) => {
    let outputDiv = <></>;
    switch (item.feature) {
      case "request_create":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-primary-purple-600 tw-text-label-sm">
              {item?.ui_id} <span className="tw-text-gray-900"> Classification Request(s) Submitted</span>
            </span>
          </div>
        );
        break;
      case "es_create":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-gray-900 tw-text-label-sm">
              {`Escalation `}
              <span className="tw-text-primary-purple-600">{item?.ui_id}</span>
              {` Raised by ${item?.created_by}`}
            </span>
          </div>
        );
        break;
      case "request_assign":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-primary-purple-600 tw-text-label-sm">
              {item?.ui_id} <span className="tw-text-gray-900">: New Classification Request Assigned</span>
            </span>
          </div>
        );
        break;
      case "es_complete":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-gray-900 tw-text-label-sm">
              {`Escalation `}
              <span className="tw-text-primary-purple-600">{item?.ui_id}</span>
              {` Resolved by ${item?.assigned_to}`}
            </span>
          </div>
        );
        break;
      case "request_complete":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-primary-purple-600 tw-text-label-sm">
              {item?.ui_id} <span className="tw-text-gray-900">: Classification Request Completed</span>
            </span>
          </div>
        );
        break;
      case "duties_update":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-label-sm tw-text-gray-900">
              HS Code <span className="tw-font-medium">{item?.data?.hs}</span> tariffs and duties structure has been updated for {item?.data?.country_desc}{" "}
              {item?.data?.direction === "export" ? "Exports" : "Imports"}. {item?.data?.duties_count} trade lane(s) in the Tariffs and Duties Watchlist has been affected
            </span>
          </div>
        );
        break;
      case "controls_update":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-label-sm tw-text-gray-900">
              HS Code <span className="tw-font-medium">{item?.data?.hs}</span> controls and license requirements have been updated for {item?.data?.country_desc}{" "}
              {item?.data?.direction === "export" ? "Exports" : "Imports"}. {item?.data?.controls_count} trade lane(s) in the Controls and License Watchlist has been affected
            </span>
          </div>
        );
        break;
      case "invalid_classification_update":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-label-sm tw-text-gray-900">
              HS Code <span className="tw-font-medium">{item?.data?.hs}</span> description has been updated for {item?.data?.country_desc}{" "}
              {item?.data?.direction === "export" ? "Exports" : "Imports"}. {item?.data?.sku_count} SKU(s) in the Invalid Classification Watchlist has been affected
            </span>
          </div>
        );
        break;
      case "invalid_classification_delete":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-label-sm tw-text-gray-900">
              HS Code <span className="tw-font-medium">{item?.data?.hs}</span> has been deleted for {item?.data?.country_desc}{" "}
              {item?.data?.direction === "export" ? "Exports" : "Imports"}.
            </span>
          </div>
        );
        break;
      case "rfi_message":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-primary-purple-600 tw-text-label-sm">
              {item?.ui_id} <span className="tw-text-gray-900">RFI Request Responded</span>
            </span>
          </div>
        );
        break;
      case "rfi_create":
        outputDiv = (
          <div className="tw-flex tw-justify-center tw-mb-0 tw-flex-col tw-space-y-1">
            <span className="tw-text-primary-purple-600 tw-text-label-sm">
              {item?.ui_id} <span className="tw-text-gray-900">RFI Request raised</span>
            </span>
          </div>
        );
        break;
      default:
        outputDiv = <span>Unable to show this notification</span>;
    }
    return outputDiv;
  };

  const handleChildNavigation = (e: React.MouseEvent, item: NotificationItem, activeTab: string, activeSubTab: string) => {
    e.stopPropagation();
    if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
    navigate("/watchlist", {
      state: {
        activeTab,
        activeSubTab,
      },
    });
    setShowNotification(false);
  };

  const renderInvalidClassificationDeleteChildren = (item: NotificationItem) => {
    const { sku_count, duties_count, controls_count } = item?.data || {};
    const children: ChildNavigationItem[] = [];
    if (sku_count && sku_count > 0) {
      children.push({
        label: `${sku_count} SKU${sku_count > 1 ? "s" : ""} affected in the Invalid Classification Watchlist.`,
        activeTab: "invalidClassification",
        activeSubTab: "active",
      });
    }
    if (duties_count && duties_count > 0) {
      children.push({
        label: `${duties_count} Trade Lane${duties_count > 1 ? "s" : ""} Disabled in the Tariffs & Duties Watchlist.`,
        activeTab: "duties",
        activeSubTab: "disabled",
      });
    }
    if (controls_count && controls_count > 0) {
      children.push({
        label: `${controls_count} Trade Lane${controls_count > 1 ? "s" : ""} Disabled in the Controls & Licenses Watchlist.`,
        activeTab: "controls",
        activeSubTab: "disabled",
      });
    }
    if (children.length === 0) return null;
    return (
      <div className="tw-ml-4 tw-mt-1 tw-border-gray-200 tw-pl-3 tw-flex tw-flex-col tw-gap-1">
        {children.map((child, idx) => (
          <div
            key={idx}
            onClick={(e) => handleChildNavigation(e, item, child.activeTab, child.activeSubTab)}
            className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-py-1 tw-pr-1 tw-rounded tw-group">
            <span className="tw-text-label-sm tw-text-gray-600">{child.label}</span>
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
              <path d="M216-144q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h264v72H216v528h528v-264h72v264q0 29.7-21.15 50.85Q773.7-144 744-144H216Zm171-192-51-51 357-357H576v-72h240v240h-72v-117L387-336Z" />
            </svg>
          </div>
        ))}
      </div>
    );
  };

  const handleNavigation = (item: NotificationItem) => {
    const currentPath = location.pathname;
    switch (item.feature) {
      case "request_create":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        if (currentPath === "/all-requests") {
          navigate(`/all-requests?refresh=${Date.now()}`);
        } else {
          navigate(`/all-requests`);
        }
        setShowNotification(false);
        break;
      case "es_create":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate(`/my-tasks/escalations/${item.ui_id}?type=${item?.type}`);
        setShowNotification(false);
        break;
      case "request_assign":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate(`/my-tasks/classification-requests/${item?.ui_id}?cr_id=${item?.redirect_id}&type=${item?.type}`);
        setShowNotification(false);
        break;
      case "es_complete":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate(`/my-tasks/classification-requests/${item?.ui_id}?cr_id=${item?.redirect_id}&type=${item?.type}`);
        setShowNotification(false);
        break;
      case "request_complete": {
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        const typeToTab: Record<string, string> = { sku: "product_attributes", coo: "bill_of_materials", fta: "fta_qualification" };
        const autoTab = item?.type ? typeToTab[item.type] : undefined;
        navigate(`/all-skus/${item?.redirect_id}${autoTab ? `?autoTab=${autoTab}` : ""}`);
        setShowNotification(false);
        break;
      }
      case "duties_update":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate("/watchlist", { state: { activeTab: "duties", activeSubTab: "active" } });
        setShowNotification(false);
        break;
      case "controls_update":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate("/watchlist", { state: { activeTab: "controls", activeSubTab: "active" } });
        setShowNotification(false);
        break;
      case "invalid_classification_update":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate("/watchlist", { state: { activeTab: "invalidClassification", activeSubTab: "active" } });
        setShowNotification(false);
        break;
      case "invalid_classification_delete":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate("/watchlist", { state: { activeTab: "invalidClassification", activeSubTab: "active" } });
        setShowNotification(false);
        break;
      case "rfi_message":
      case "rfi_create":
        if (item.status === notificatonStatusEnum.unread) notificationsUpdate(notificatonActionEnum.markRead, item);
        navigate(`/my-tasks/rfi-requests/${item?.ui_id}?transaction_id=${item?.transaction_id}&type=${item?.type}&cr_id=${item?.redirect_id}`, {
          state: { openRfiDrawer: true },
        });
        setShowNotification(false);
        break;
      default:
        break;
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="tw-absolute tw-w-96 tw-max-h-96 tw-rounded-sm tw-shadow-countryHs !tw-border !tw-border-gray-200 tw-bg-white tw-text-black tw-top-9 tw-right-0">
      <div className="tw-flex tw-items-center tw-w-full tw-border-b tw-border-gray-200 tw-py-2 tw-px-4">
        <div className="tw-flex tw-gap-3">
          <span className="tw-text-label-md tw-font-semibold">Notification Center</span>
        </div>
      </div>
      <div className="tw-px-3 tw-py-1 tw-max-h-72 tw-overflow-auto">
        {modalData && modalData.length > 0 ? (
          modalData.map((item, index) => (
            <div
              onClick={item.feature === "invalid_classification_delete" ? undefined : () => handleNavigation(item)}
              className={`tw-border-b tw-border-gray-200 tw-py-2 ${!showLoadMore ? "last:tw-border-none" : ""} ${item.feature === "invalid_classification_delete" ? "tw-cursor-default" : "tw-cursor-pointer"}`}
              key={item.feature + index}>
              <p className="tw-flex tw-mb-0">
                <span
                  className={
                    item.status === "read"
                      ? "tw-flex tw-flex-shrink-0 tw-rounded-full tw-w-1.5 tw-h-1.5 tw-flex-grow-0 tw-bg-gray-300 tw-m-1"
                      : "tw-flex tw-flex-shrink-0 tw-rounded-full tw-w-1.5 tw-h-1.5 tw-flex-grow-0 tw-bg-warning-500 tw-m-1"
                  }></span>
                {setHeadingHandler(item)}
              </p>
              <p className="tw-text-gray-500 tw-text-label-sm tw-mb-0 tw-pb-2 tw-m-1">{item?.updated_at}</p>
              {item.feature === "invalid_classification_delete" && renderInvalidClassificationDeleteChildren(item)}
            </div>
          ))
        ) : (
          <div className="tw-py-6 tw-flex tw-items-center tw-justify-center tw-gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#98A2B3">
              <path d="M160-200v-80h80v-280q0-33 8.5-65t25.5-61l60 60q-7 16-10.5 32.5T320-560v280h248L56-792l56-56 736 736-56 56-146-144H160Zm560-154-80-80v-126q0-66-47-113t-113-47q-26 0-50 8t-44 24l-58-58q20-16 43-28t49-18v-28q0-25 17.5-42.5T480-880q25 0 42.5 17.5T540-820v28q80 20 130 84.5T720-560v206Zm-276-50Zm36 324q-33 0-56.5-23.5T400-160h160q0 33-23.5 56.5T480-80Zm33-481Z" />
            </svg>
            <span className="tw-text-label-md tw-font-medium tw-text-gray-900">No New Notifications</span>
          </div>
        )}

        {showLoadMore && (
          <div
            className="tw-flex tw-items-center tw-py-2 tw-cursor-pointer tw-text-label-sm tw-font-medium tw-text-primary-purple-600"
            onClick={!isFetchingMore ? loadMoreHandler : undefined}>
            {isFetchingMore ? <span className="tw-cursor-not-allowed tw-opacity-50">Loading...</span> : "Load More"}
          </div>
        )}
      </div>
      {modalData && modalData.length > 0 ? (
        <div className="tw-flex tw-justify-between tw-px-4 tw-py-2 tw-border-t tw-border-gray-200">
          {unreadCount !== undefined && unreadCount > 0 && (
            <span
              onClick={() => {
                setShowNotification(false);
                notificationsUpdate(notificatonActionEnum.markAllRead, null);
              }}
              className="tw-text-purple-600 tw-cursor-pointer tw-text-label-md">
              Mark all as read
            </span>
          )}
          {totalCount !== undefined && totalCount > 0 && (
            <span
              onClick={() => {
                setShowNotification(false);
                notificationsUpdate(notificatonActionEnum.clearAllRead, null);
              }}
              className="tw-text-purple-600 tw-cursor-pointer tw-text-label-md">
              Clear All
            </span>
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default NotificationModal;
